import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateServicesMutation } from '../../api/account/accountApiSlice';
import { TenantService, isEnableableService, keptOnlyServices, setStoredServices } from '../../common/selfServiceSolutions';
import { liveUseCasesWhere } from '../../utils/useCases';
import { setConfig } from '../../store/slices/authenticationSlice';
import { RootState } from '../../store/store';

/** The kolula ROOM switches: every enableable live use case of the space
 *  template (`utils/useCases.ts`), so a room use case going live is offered
 *  here without a list to extend. */
const ROOM_SWITCHES: Array<{ service: TenantService }> = liveUseCasesWhere(
    (d) => d.template === 'space' && d.enableable,
).map((service) => ({ service }));

/** Trial gating (PO, 2026-08-18): ONE use case — together with the existing
 *  object limit (`tenant.max_objects`), both axes. Mirror of the backend's
 *  TRIAL_MAX_SERVICES, which enforces it. */
const TRIAL_MAX_SERVICES = 1;

/** Same set of use cases, whatever the order they were ticked in. */
const byName = (x: string, y: string) => x.localeCompare(y);
const sameServices = (a: string[], b: string[]) =>
    [...a].sort(byName).join(',') === [...b].sort(byName).join(',');

type UseCasesSectionProps = {
    readOnly: boolean;
    /** Resolved services of the tenant IN VIEW — passed in, never re-queried
     *  (#1334): a bare useGetAccountQuery here would show a master their own
     *  tenant's use cases under another tenant's name. */
    services: string[] | null;
    /** False while `services` is still the solution-derived fallback — the
     *  tenant has never chosen. Surfaced instead of passing silently. */
    servicesConfigured: boolean;
    /** Trial tenants get exactly one use case. */
    isTrial: boolean;
    /** Identity of the tenant in view; a change drops an open draft. */
    tenantKey: string | undefined;
    /** Reports whether the open draft differs from what it started from. */
    onDirtyChange?: (dirty: boolean) => void;
};

/**
 * Which use cases the tenant offers — the enable switch (phase 2 of
 * `docs/plans/service-configuration-area.md`).
 *
 * It lives in ACCOUNT, deliberately its own tab: under the dividing rule
 * (CEO, 2026-08-17) anything that leads to gating is resolved in Account, and
 * the enable switch is what a plan can be billed against. Configuring the
 * enabled use case happens in Services; enabling it here is what makes its
 * section appear there, and its card appear in the end-user app.
 *
 * What is offered (PO, 2026-08-18): venue, guest apartment, product rental,
 * SUP rental and storage — presented in one block per marketing product
 * (PO, 2026-08-19; see PRODUCT_BLOCKS below). Key handover follows in a fast
 * successive step; generic handover and parcel reception are set aside.
 * A service the tenant already carries from before this gate existed stays
 * keepable even when it is not enableable, so saving never silently strips a
 * live section.
 */
const UseCasesSection = ({
    readOnly,
    services,
    servicesConfigured,
    isTrial,
    tenantKey,
    onDirtyChange,
}: UseCasesSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateServices, { isLoading: saving }] = useUpdateServicesMutation();
    const currentConfig = useSelector((state: RootState) => state.authentication.config);

    const stored = useMemo(
        () => (services ?? []).map((s) => s.toLowerCase()),
        [services],
    );

    // One editable unit (design-decisions › Bearbeiten und Speichern): the view
    // shows the stored use cases, "Bearbeiten" opens a draft of them. Dirty is
    // order-insensitive — re-ticking what was unticked makes it clean again.
    const draft = useEditableDraft<string[]>(stored, sameServices);
    const selected = draft.value;
    const [error, setError] = useState<string | null>(null);

    // A tenant switch drops an open draft rather than carrying it over.
    const { cancel } = draft;
    useEffect(() => {
        cancel();
        setError(null);
    }, [tenantKey, cancel]);

    // Kept-but-not-enableable services: shown locked-on rather than hidden.
    const keptOnly = keptOnlyServices(stored);

    // The Account page's leave guard reads this. An unmounted section has no
    // draft any more (it is discarded with the section), so it reports clean.
    const { isDirty } = draft;
    useEffect(() => {
        onDirtyChange?.(isDirty);
        return () => onDirtyChange?.(false);
    }, [isDirty, onDirtyChange]);

    const atTrialLimit = isTrial
        && selected.filter((s) => isEnableableService(s)).length
            >= TRIAL_MAX_SERVICES;

    const toggle = (service: TenantService) => {
        draft.setDraft(
            selected.includes(service)
                ? selected.filter((s) => s !== service)
                : [...selected, service],
        );
    };

    // A never-configured tenant sees its derived list pre-selected; saving it
    // unchanged is exactly the act of confirming, so Speichern is enabled
    // then (the "never saved" exception of the save rule).
    const saveable = isDirty || (draft.editing && !servicesConfigured);

    const handleSave = async () => {
        setError(null);
        try {
            const res = await updateServices({ services: selected }).unwrap();
            const next = (res.tenant.services ?? selected) as TenantService[];
            // The sidebar and the create-booking options read the Redux config,
            // which is synthesized per tenant switch, not per account refetch —
            // update it (and its localStorage cache) here so the change shows
            // without a reload. Spread the current config so logo and name
            // survive; only the services move.
            setStoredServices(next);
            if (currentConfig) {
                dispatch(setConfig({
                    ...currentConfig,
                    services: next,
                    createBookingOptions: next,
                }));
            }
            draft.finish();
        } catch (e) {
            const message = (e as { data?: { message?: string } })?.data?.message;
            setError(message ?? t('account.use_cases_save_failed'));
        }
    };

    const renderSwitch = (service: TenantService, labelKey: string | undefined, locked: boolean) => {
        const checked = selected.includes(service);
        // The trial limit is a hint for choosing, so only while editing.
        const blocked = !locked && !checked && atTrialLimit;
        return (
            <label
                key={service}
                className={'flex items-start gap-2 text-sm text-foreground'}
            >
                <input
                    type={'checkbox'}
                    className={'mt-1'}
                    checked={checked}
                    disabled={locked || saving || blocked}
                    onChange={() => toggle(service)}
                />
                <span>
                    {labelKey
                        ? t(labelKey)
                        : t(`service_type_selector.${service}`, { defaultValue: service })}
                    {blocked && (
                        <span className={'block text-xs text-foreground/60'}>
                            {t('account.use_cases_trial_limit')}
                        </span>
                    )}
                </span>
            </label>
        );
    };

    const renderKept = (service: string) => (
        <label key={service} className={'flex items-start gap-2 text-sm text-foreground'}>
            <input type={'checkbox'} className={'mt-1'} checked disabled />
            <span>
                {t(`service_type_selector.${service}`, { defaultValue: service })}
                <span className={'block text-xs text-foreground/60'}>
                    {t('account.use_cases_kept_hint')}
                </span>
            </span>
        </label>
    );

    /**
     * The tab is structured by MARKETING PRODUCT (PO, 2026-08-19, staging
     * review): one block per kolula product, its use cases beneath. SUP rental
     * is its own use case under kolula SUP rather than a sub-point of product
     * rental — the earlier indentation was technically apt (SUP is a specific
     * rental form) but read as if SUP came along with product rental for free,
     * when it has to be chosen explicitly.
     *
     * The block titles are brand names, identical in every language by
     * definition — which is why they are constants here and not i18n keys, and
     * why the product word is uppercase while `kolula` stays lowercase.
     * `keptOnly` names the pre-gate services that belong to the block; the
     * block shows them locked-on when the tenant still carries them.
     */
    const PRODUCT_BLOCKS: Array<{
        name: string;
        switches: Array<{ service: TenantService, labelKey?: string }>;
        keptHome: string[];
        /** Use cases announced but not yet enableable — a locked catalog entry
         *  ("Available shortly"). PO, 2026-08-19: the tab is the product
         *  catalog, so an imminent product is shown where products are chosen.
         *  Deliberately NOT the missing-value "Coming soon" the visual rules
         *  ban — this is a scheduled product, not absent data — and only for
         *  the decided fast-follow (keys), never for the set-aside use cases. */
        upcoming: Array<{ service: string }>;
    }> = [
        // The room use cases a tenant may switch on, in description order:
        // venue, apartment and sports halls (hall, PO decision 6, 2026-10-18).
        { name: 'kolula ROOM', switches: ROOM_SWITCHES, keptHome: [], upcoming: [] },
        { name: 'kolula STATION', switches: [{ service: 'rental' }, { service: 'storage' }], keptHome: ['p2p', 'parcel'], upcoming: [] },
        { name: 'kolula SUP', switches: [{ service: 'sup', labelKey: 'account.use_cases_sup_label' }], keptHome: [], upcoming: [] },
        // Key handover is the decided fast-follow: shown locked until `keys`
        // is `enableable` in the use-case description (a one-line change there unlocks it here).
        { name: 'kolula KEY', switches: [], keptHome: ['keys'], upcoming: [{ service: 'keys' }] },
    ];

    const renderUpcoming = (service: string) => (
        <label key={`upcoming-${service}`} className={'flex items-start gap-2 text-sm text-foreground/50'}>
            <input type={'checkbox'} className={'mt-1'} checked={false} disabled />
            <span>
                {t(`service_type_selector.${service}`, { defaultValue: service })}
                <span className={'block text-xs text-foreground/60'}>
                    {t('account.use_cases_upcoming')}
                </span>
            </span>
        </label>
    );

    /** The product blocks — locked in the view, editable in the draft. */
    const renderBlocks = (locked: boolean) => (
        <div className={'flex flex-col gap-6'}>
            {!servicesConfigured && (
                <p className={'text-sm text-foreground/60 rounded-[5px] border border-foreground/10 bg-foreground/5 px-3 py-2'}>
                    {t('account.use_cases_unconfigured')}
                </p>
            )}

            {PRODUCT_BLOCKS.map((block) => {
                const keptHere = keptOnly.filter((s) => block.keptHome.includes(s));
                // An announced use case a tenant already carries renders as its
                // kept row, not twice.
                const upcomingHere = block.upcoming.filter(
                    ({ service }) => !stored.includes(service),
                );
                if (block.switches.length === 0 && keptHere.length === 0
                    && upcomingHere.length === 0) return null;
                return (
                    <div key={block.name}>
                        <h3 className={'text-sm font-semibold text-foreground mb-3'}>
                            {block.name}
                        </h3>
                        <div className={'flex flex-col gap-2'}>
                            {block.switches.map(({ service, labelKey }) =>
                                renderSwitch(service, labelKey, locked))}
                            {keptHere.map(renderKept)}
                            {upcomingHere.map(({ service }) => renderUpcoming(service))}
                        </div>
                    </div>
                );
            })}

            <p className={'text-xs text-foreground/60'}>
                {t('account.use_cases_upcoming_hint')}
            </p>
        </div>
    );

    return (
        <EditableSection
            id={'use-cases'}
            title={t('account.use_cases_section')}
            description={t('account.use_cases_section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={saveable}
            saving={saving}
            canSave={selected.length > 0}
            error={error}
            onEdit={() => { setError(null); draft.startEdit(); }}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void handleSave(); }}
            view={renderBlocks(true)}
        >
            {renderBlocks(false)}
        </EditableSection>
    );
};

export default UseCasesSection;
